import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LIVE_REFRESH_MS } from "@/lib/reporting";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, inputErrorCls, PrimaryButton, EmptyState } from "@/components/lims";
import SampleQrButton from "@/components/SampleQr";
import ScanLookup from "@/components/ScanLookup";
import SampleLocation from "@/components/SampleLocation";
import { sampleIdFromScan } from "@/lib/scan";
import { Plus, TestTube, MagnifyingGlass, Trash, PencilSimple, List, SquaresFour, ClockCounterClockwise, DownloadSimple, Rows, Export } from "@phosphor-icons/react";
import { can, canDeleteSamples, canRegisterSamples } from "@/lib/roles";

// Fallback until the configured list loads (NFR-14: the real vocabulary is
// laboratory configuration served by /sample-types).
const SAMPLE_TYPES = ["Blood", "Plasma", "Serum", "DNA", "RNA", "Tissue", "Cell Line", "Urine", "Saliva", "Other"];
// FR-04 lifecycle vocabulary — status is read-only everywhere; it moves only
// through the named transitions the server offers per sample (UX-06).
const STATUSES = ["Received", "In Testing", "In Review", "Released", "On Hold", "Disposed"];

const todayStr = () => new Date().toISOString().slice(0, 10);
const empty = () => ({
  name: "", client_id: "", sample_type: "Blood", received_date: todayStr(), description: "",
  required_tests: [], project: "", source: "", volume: "", unit: "mL", concentration: "",
  collection_date: "", received_at: null, order_id: "", box_id: "", position: "", notes: "",
});

const nullable = (value) =>
  value === null || value === undefined ||
  (typeof value === "string" && value.trim() === "")
    ? null
    : value;

/**
 * A select sizes itself to its widest option, and the transitions on offer
 * differ by lifecycle state, so every row produced a control of a different
 * width and the column edge moved by up to 29px between adjacent rows. The
 * closed control only ever reads "Move…", so a fixed width costs nothing and
 * the column lines up.
 */
function TransitionControl({ sample, user, onStart }) {
  const options = (sample.allowed_transitions || []).filter((t) => can(user, t.permission));
  if (options.length === 0) return null;
  return (
    <select
      data-testid={`transition-${sample.sample_code}`}
      value=""
      aria-label={`Move ${sample.sample_code}`}
      onChange={(e) => {
        const t = options.find((o) => o.action === e.target.value);
        if (t) onStart(sample, t);
      }}
      className="w-32 shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 transition-colors"
    >
      <option value="" disabled>Move…</option>
      {options.map((t) => (
        <option
          key={t.action}
          value={t.action}
          label={`${t.action} → ${t.to}`}
        />
      ))}
    </select>
  );
}

// FR-01: these checks mirror the server's rules so refusals surface inline
// instead of as a toast; the server remains the authority.
function validate(form, editing) {
  const errors = {};
  if (!form.client_id) errors.client_id = "Select the client this sample belongs to.";
  if (!form.sample_type) errors.sample_type = "Sample type is required.";
  if (!form.received_date) errors.received_date = "Received date is required.";
  else if (form.received_date > todayStr()) errors.received_date = "Received date cannot be in the future.";
  if (!editing && form.required_tests.length === 0) errors.required_tests = "Select at least one required test.";
  return errors;
}

// Map FastAPI 422 details onto form fields; anything unmappable becomes a toast.
function serverFieldErrors(detail) {
  const errors = {};
  if (Array.isArray(detail)) {
    detail.forEach((e) => {
      const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : null;
      if (typeof field === "string" && typeof e.msg === "string") errors[field] = e.msg.replace(/^Value error, /, "");
    });
  }
  return errors;
}

export default function Samples() {
  const { user } = useAuth();
  const canManage = canDeleteSamples(user);
  const canEdit = canRegisterSamples(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [clients, setClients] = useState([]);
  const [assays, setAssays] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("table");
  const [showVoided, setShowVoided] = useState(false);
  const [sampleTypes, setSampleTypes] = useState(SAMPLE_TYPES);
  const [pendingTransition, setPendingTransition] = useState(null); // {sample, t} or {samples, action} for bulk
  const [transitionReason, setTransitionReason] = useState("");
  const [transitionError, setTransitionError] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchShared, setBatchShared] = useState({ client_id: "", received_date: todayStr(), sample_type: "Serum", description: "" });
  const [batchTests, setBatchTests] = useState([]);
  const [batchRows, setBatchRows] = useState("");
  const [batchResult, setBatchResult] = useState(null);
  const [batchSaving, setBatchSaving] = useState(false);

  const load = async (voidedOnly = false, quiet = false) => {
    if (!quiet) setLoading(true);
    const { data } = await api.get(voidedOnly ? "/samples?voided_only=true" : "/samples");
    setSamples(data);
    if (!quiet) setLoading(false);
  };
  const reload = () => load(showVoided);

  const loadRefs = async () => {
    const [c, a, o, f] = await Promise.all([
      api.get("/clients"), api.get("/catalogue?active_only=true"), api.get("/orders"), api.get("/freezers"),
    ]);
    setClients(c.data);
    setAssays(a.data);
    setOrders(o.data);
    api.get("/sample-types?active_only=true")
      .then(({ data }) => { if (data.length > 0) setSampleTypes(data.map((t) => t.name)); })
      .catch(() => {});
    const flat = [];
    f.data.forEach((fz) => fz.racks?.forEach((r) => r.boxes?.forEach((b) =>
      flat.push({ id: b.id, label: `${fz.name} / ${r.name} / ${b.name}` }))));
    setBoxes(flat);
  };

  useEffect(() => { load(); loadRefs(); }, []);

  useEffect(() => {
    const timer = window.setInterval(
      () => load(showVoided, true),
      LIVE_REFRESH_MS,
    );
    return () => window.clearInterval(timer);
  }, [showVoided]);

  // A scan is a different question from a search. A handheld scanner in
  // keyboard mode types the code's detail URL into whatever field has focus,
  // and that URL matches no name, code, client, or project — so scanning used
  // to return nothing. Recognising it narrows the list to that one record
  // instead. The status filter is ignored for a scan: you scanned a specific
  // tube, and hiding it because a filter is set would look like a lost sample.
  const scannedId = useMemo(() => sampleIdFromScan(search), [search]);

  const filtered = useMemo(() => {
    if (scannedId) return samples.filter((s) => s.id === scannedId);
    return samples.filter((s) => {
      const q = search.toLowerCase();
      const matchQ = !q || s.name?.toLowerCase().includes(q) || s.sample_code?.toLowerCase().includes(q) ||
        s.project?.toLowerCase().includes(q) || s.client_name?.toLowerCase().includes(q);
      const matchS = !statusFilter || s.status === statusFilter;
      return matchQ && matchS;
    });
  }, [samples, search, statusFilter, scannedId]);

  const openNew = () => { setEditing(null); setForm(empty()); setErrors({}); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...empty(),
      ...s,
      client_id: s.client_id || "",
      received_date: s.received_date || "",
      description: s.description || "",
      name: s.name || "",
      project: s.project || "",
      source: s.source || "",
      volume: s.volume ?? "",
      unit: s.unit ?? "",
      concentration: s.concentration || "",
      collection_date: s.collection_date || "",
      order_id: s.order_id || "",
      box_id: s.box_id || "",
      position: s.position || "",
      notes: s.notes || "",
    });
    setErrors({});
    setOpen(true);
  };

  useEffect(() => {
    if (searchParams.get("register") !== "1") return;

    const next = new URLSearchParams(searchParams);
    next.delete("register");
    setSearchParams(next, { replace: true });

    if (canEdit) {
      setEditing(null);
      setForm(empty());
      setErrors({});
      setOpen(true);
    }
  }, [canEdit, searchParams, setSearchParams]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((errs) => ({ ...errs, [k]: undefined }));
  };

  const toggleRequiredTest = (assayId) => {
    setForm((f) => ({
      ...f,
      required_tests: f.required_tests.includes(assayId)
        ? f.required_tests.filter((id) => id !== assayId)
        : [...f.required_tests, assayId],
    }));
    setErrors((errs) => ({ ...errs, required_tests: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form, editing);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return; // AC-3: inline errors block submission
    }
    setSaving(true);
    const payload = {
      client_id: form.client_id,
      sample_type: form.sample_type,
      received_date: form.received_date,
      description: nullable(form.description),
      name: nullable(form.name),
      project: nullable(form.project),
      source: nullable(form.source),
      volume: nullable(form.volume) === null ? null : Number.parseFloat(form.volume),
      unit: nullable(form.unit),
      concentration: nullable(form.concentration),
      collection_date: nullable(form.collection_date),
      received_at: nullable(form.received_at),
      order_id: nullable(form.order_id),
      box_id: nullable(form.box_id),
      position: nullable(form.position),
      notes: nullable(form.notes),
    };
    try {
      if (editing) {
        await api.put(`/samples/${editing.id}`, payload);
        toast.success("Sample updated");
      } else {
        await api.post("/samples", {
          ...payload,
          required_tests: form.required_tests,
        });
        toast.success("Sample registered");
      }
      setOpen(false);
      reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const mapped = serverFieldErrors(detail);
      if (Object.keys(mapped).length > 0) setErrors((errs) => ({ ...errs, ...mapped }));
      else toast.error(formatApiError(detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!(await voidRecord(`/samples/${s.id}`, `${s.sample_code} and its tests`))) return;
    toast.success("Sample voided");
    reload();
  };

  const [custody, setCustody] = useState(null); // {sample_code, events, ...}
  const [custodyLoading, setCustodyLoading] = useState(false);

  const openCustody = async (s) => {
    setCustodyLoading(true);
    setCustody({ sample_code: s.sample_code, events: [] });
    try {
      const { data } = await api.get(`/samples/${s.id}/custody`);
      setCustody(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setCustody(null);
    } finally {
      setCustodyLoading(false);
    }
  };

  const downloadCustodyCsv = async () => {
    const { data } = await api.get(`/samples/${custody.sample_id}/custody?format=csv`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `custody_${custody.sample_code}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const startTransition = (sample, t) => {
    setPendingTransition({ sample, t });
    setTransitionReason("");
    setTransitionError("");
  };

  const startBulkTransition = (action) => {
    if (selected.size === 0) return;
    setPendingTransition({ bulk: true, action, count: selected.size });
    setTransitionReason("");
    setTransitionError("");
  };

  const confirmTransition = async (e) => {
    e.preventDefault();
    setTransitioning(true);
    try {
      if (pendingTransition.bulk) {
        const { data } = await api.post("/samples/bulk-transition", {
          ids: [...selected],
          action: pendingTransition.action,
          reason: transitionReason.trim() || null,
        });
        if (data.errors.length > 0 && data.moved.length === 0 &&
            data.errors.every((x) => x.detail === data.errors[0].detail)) {
          setTransitionError(data.errors[0].detail);
          return;
        }
        toast.success(`${pendingTransition.action}: ${data.moved.length} moved${data.errors.length ? `, ${data.errors.length} skipped` : ""}`);
        if (data.errors.length > 0) {
          data.errors.slice(0, 3).forEach((x) => toast.error(`${x.sample_code || x.id}: ${x.detail}`));
        }
        setSelected(new Set());
      } else {
        const { sample, t } = pendingTransition;
        if (t.reason_required && !transitionReason.trim()) {
          setTransitionError("A reason is required for this transition.");
          return;
        }
        await api.post(`/samples/${sample.id}/transition`, {
          action: t.action, reason: transitionReason.trim() || null,
        });
        toast.success(`${t.action}: ${sample.sample_code} is now ${t.to}`);
      }
      setPendingTransition(null);
      reload();
    } catch (err) {
      setTransitionError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setTransitioning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => prev.size === filtered.length
      ? new Set()
      : new Set(filtered.map((s) => s.id)));
  };

  // UX-12: CSV export of exactly what the current filters show.
  const exportCsv = () => {
    const cols = ["sample_code", "name", "client_name", "sample_type", "received_date", "status", "location_label", "description"];
    const lines = [cols.join(",")].concat(filtered.map((s) =>
      cols.map((c) => `"${String(s[c] ?? "").replace(/"/g, '""')}"`).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n") + "\n"], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "samples.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitBatch = async (e) => {
    e.preventDefault();
    const rows = batchRows.split("\n").map((r) => r.trim()).filter(Boolean).map((line) => {
      // Paste/CSV format: name[,sample_type[,position]]
      const [name, sample_type, position] = line.split(",").map((x) => x.trim());
      return { name: name || null, sample_type: sample_type || null, position: position || null };
    });
    if (!batchShared.client_id || rows.length === 0 || batchTests.length === 0) {
      toast.error("Batch needs a client, at least one required test, and at least one row.");
      return;
    }
    setBatchSaving(true);
    try {
      const { data } = await api.post("/samples/batch", {
        ...batchShared,
        required_tests: batchTests,
        samples: rows,
      });
      setBatchResult(data);
      if (data.created_count > 0) reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBatchSaving(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title="Samples"
        subtitle="Register and track specimens across their lifecycle"
        action={canEdit ? (
          <div className="flex gap-2">
            <button data-testid="batch-register-button" onClick={() => { setBatchOpen(true); setBatchResult(null); }} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white text-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors">
              <Rows size={18} /> Batch
            </button>
            <PrimaryButton data-testid="new-sample-button" onClick={openNew}><Plus size={18} /> Register Sample</PrimaryButton>
          </div>
        ) : null}
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input aria-label="Search samples" data-testid="sample-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search or scan a code…" className={`${inputCls} pl-9`} />
        </div>
        <ScanLookup onFound={(id) => navigate(`/samples/${id}`)} />
        <select aria-label="Filter samples by status" data-testid="sample-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} max-w-[180px]`}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid="export-csv-button" onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
          <Export size={16} /> CSV
        </button>
        <label className="flex items-center gap-2 text-sm text-slate-600 px-2">
          <input
            type="checkbox"
            data-testid="show-voided-toggle"
            checked={showVoided}
            onChange={(e) => { setShowVoided(e.target.checked); load(e.target.checked); }}
            className="rounded border-slate-300"
          />
          Voided only
        </label>
        <div className="flex rounded-md border border-slate-200 overflow-hidden" role="group" aria-label="View">
          <button type="button" data-testid="samples-view-table" onClick={() => setView("table")} aria-pressed={view === "table"}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <List size={16} /> Table
          </button>
          <button type="button" data-testid="samples-view-board" onClick={() => setView("board")} aria-pressed={view === "board"}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === "board" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <SquaresFour size={16} /> Board
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-bar" className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-md bg-slate-900 text-white text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <select
            data-testid="bulk-transition-select"
            value=""
            aria-label="Bulk lifecycle action"
            onChange={(e) => { if (e.target.value) startBulkTransition(e.target.value); }}
            className="rounded-md bg-slate-800 border border-slate-700 px-2 py-1.5 text-xs text-white"
          >
            <option value="" disabled>Lifecycle action…</option>
            {["Start Testing", "Submit for Review", "Place on Hold", "Resume", "Dispose"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-slate-300 hover:text-white">Clear</button>
        </div>
      )}

      {view === "board" && !loading && (
        <div data-testid="samples-board" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
          {STATUSES.map((status) => {
            const group = filtered.filter((s) => s.status === status);
            return (
              <div key={status} className="bg-slate-50 rounded-lg border border-slate-200 p-3 min-h-[120px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{status}</span>
                  <span className="text-xs text-slate-400">{group.length}</span>
                </div>
                <div className="space-y-2">
                  {group.map((s) => (
                    <div key={s.id} data-testid={`board-card-${s.sample_code}`} className="bg-white rounded-md border border-slate-200 p-2.5 shadow-sm">
                      <div className="flex items-center justify-between gap-1">
                        <Link to={`/samples/${s.id}`} className="font-mono text-[11px] text-slate-400 hover:text-slate-700 hover:underline">{s.sample_code}</Link>
                        <SampleQrButton sampleId={s.id} sampleCode={s.sample_code} />
                      </div>
                      <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.client_name || "—"}</div>
                      <div className="mt-2"><TransitionControl sample={s} user={user} onStart={startTransition} /></div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={view === "board" ? "hidden" : "bg-white rounded-lg border border-slate-200 overflow-x-auto"}>
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={TestTube} title="No samples found" subtitle="Register your first specimen to get started" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    data-testid="select-all-samples"
                    aria-label="Select all samples"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300"
                  />
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Code</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Received</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Status</th>
                <th className="px-4 py-3 font-semibold text-right whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} data-testid={`sample-row-${s.sample_code}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      data-testid={`select-${s.sample_code}`}
                      aria-label={`Select ${s.sample_code}`}
                      checked={selected.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                      className="rounded border-slate-300"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap align-middle">
                    {/* The code and its QR are the same fact in two forms, so
                        they sit together rather than the QR joining the action
                        icons, which are things you do to the row. */}
                    <div className="flex items-center gap-1">
                      <Link data-testid={`sample-link-${s.sample_code}`} to={`/samples/${s.id}`} className="text-slate-600 hover:text-slate-900 hover:underline">{s.sample_code}</Link>
                      <SampleQrButton sampleId={s.id} sampleCode={s.sample_code} />
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900 align-middle">{s.name}<div className="text-xs text-slate-400 font-normal">{s.project}</div></td>
                  <td className="px-4 py-3 text-slate-600 align-middle">{s.client_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap align-middle">{s.sample_type}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap align-middle">{s.received_date || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs align-middle">
                    <SampleLocation sample={s} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    {s.voided ? (
                      <span title={s.void_reason} className="inline-flex items-center text-xs font-semibold text-status-critical bg-status-critical/10 border border-status-critical/30 px-2.5 py-1 rounded-full">VOID</span>
                    ) : (
                      <StatusBadge status={s.status} />
                    )}
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center justify-end gap-1">
                      <TransitionControl sample={s} user={user} onStart={startTransition} />
                      <button data-testid={`custody-sample-${s.sample_code}`} title="Chain of custody" aria-label={`Chain of custody for ${s.sample_code}`} onClick={() => openCustody(s)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><ClockCounterClockwise size={16} /></button>
                      {canEdit && <button data-testid={`edit-sample-${s.sample_code}`} onClick={() => openEdit(s)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><PencilSimple size={16} /></button>}
                      {canManage && <button data-testid={`delete-sample-${s.sample_code}`} onClick={() => remove(s)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Sample" : "Register Sample"} testid="sample-modal">
        <form onSubmit={submit} noValidate className="space-y-4">
          <Field label="Client" error={errors.client_id} errorTestId="sample-client-error">
            <select data-testid="sample-client-select" value={form.client_id} onChange={set("client_id")} className={`${inputCls} ${errors.client_id ? inputErrorCls : ""}`}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type" error={errors.sample_type} errorTestId="sample-type-error">
              <select data-testid="sample-type-select" value={form.sample_type} onChange={set("sample_type")} className={inputCls}>{sampleTypes.map((t) => <option key={t}>{t}</option>)}</select>
            </Field>
            <Field label="Received date" error={errors.received_date} errorTestId="sample-received-error">
              <input data-testid="sample-received-input" type="date" max={todayStr()} value={form.received_date} onChange={set("received_date")} className={`${inputCls} ${errors.received_date ? inputErrorCls : ""}`} />
            </Field>
          </div>
          <Field label="Description">
            <textarea data-testid="sample-description-input" value={form.description} onChange={set("description")} rows={2} className={inputCls} />
          </Field>
          {editing ? (
            (editing.required_tests?.length ?? 0) > 0 && (
              <Field label="Required tests (set at registration)">
                <div className="flex flex-wrap gap-1.5">
                  {editing.required_tests.map((t) => (
                    <span key={t.assay_id} className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{t.assay_name}</span>
                  ))}
                </div>
              </Field>
            )
          ) : (
            <Field label="Required tests" error={errors.required_tests} errorTestId="sample-required-tests-error">
              {assays.length === 0 ? (
                <p className="text-sm text-slate-400">No active assays yet. Add one under Test Catalog first.</p>
              ) : (
                <div className={`max-h-40 overflow-y-auto rounded-md border p-2 space-y-1 ${errors.required_tests ? "border-status-critical/30" : "border-slate-200"}`}>
                  {assays.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-slate-700 px-1 py-0.5 rounded hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`required-test-${a.assay_code}`}
                        checked={form.required_tests.includes(a.id)}
                        onChange={() => toggleRequiredTest(a.id)}
                        className="rounded border-slate-300"
                      />
                      <span>{a.name}</span>
                      {a.method && <span className="text-xs text-slate-400">{a.method}</span>}
                    </label>
                  ))}
                </div>
              )}
            </Field>
          )}
          <Field label="Sample name (optional)"><input data-testid="sample-name-input" value={form.name} onChange={set("name")} placeholder="Defaults to the sample code" className={inputCls} /></Field>
          {editing && (
            <Field label="Status (moved via lifecycle actions)">
              <div className="pt-1.5"><StatusBadge status={editing.status} /></div>
            </Field>
          )}
          {/* UX-08: storage and biobank detail is secondary to the accession record */}
          <details data-testid="storage-details-section" className="rounded-md border border-slate-200 p-3 [&[open]]:pb-4">
            <summary className="cursor-pointer text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 select-none">
              Storage &amp; biobank details (optional)
            </summary>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Project"><input value={form.project} onChange={set("project")} className={inputCls} /></Field>
                <Field label="Source / Donor"><input value={form.source} onChange={set("source")} className={inputCls} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Volume"><input type="number" step="any" value={form.volume} onChange={set("volume")} className={inputCls} /></Field>
                <Field label="Unit"><input value={form.unit} onChange={set("unit")} className={inputCls} /></Field>
                <Field label="Concentration"><input value={form.concentration} onChange={set("concentration")} className={inputCls} /></Field>
              </div>
              <Field label="Collection date (when drawn, not when received)"><input type="date" value={form.collection_date} onChange={set("collection_date")} className={inputCls} /></Field>
              <Field label="Linked order (optional)">
                <select data-testid="sample-order-select" value={form.order_id} onChange={set("order_id")} className={inputCls}>
                  <option value="">No order</option>
                  {orders.map((o) => <option key={o.id} value={o.id}>{o.order_code} · {o.client_name}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Storage box"><select data-testid="sample-box-select" value={form.box_id} onChange={set("box_id")} className={inputCls}><option value="">Unassigned</option>{boxes.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}</select></Field>
                <Field label="Position"><input value={form.position} onChange={set("position")} placeholder="e.g. A1" className={inputCls} /></Field>
              </div>
              <Field label="Notes"><textarea value={form.notes} onChange={set("notes")} rows={2} className={inputCls} /></Field>
            </div>
          </details>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-sample-button" disabled={saving}>{editing ? "Save changes" : "Register"}</PrimaryButton>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!custody}
        onClose={() => setCustody(null)}
        title={custody ? `Chain of custody: ${custody.sample_code}` : ""}
        testid="custody-modal"
      >
        {custody && (
          <div className="space-y-4">
            {custodyLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />)}</div>
            ) : (
              <>
                <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                  {custody.events.map((e, i) => (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300" aria-hidden="true" />
                      <div className="text-sm font-medium text-slate-900">{e.event}</div>
                      <div className="text-xs text-slate-500">{e.detail}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {e.actor || "—"} · {e.timestamp ? new Date(e.timestamp).toLocaleString() : "—"}
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    data-testid="custody-export-csv"
                    onClick={downloadCustodyCsv}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <DownloadSimple size={16} /> Export CSV
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={!!pendingTransition}
        onClose={() => setPendingTransition(null)}
        title={pendingTransition
          ? pendingTransition.bulk
            ? `${pendingTransition.action}: ${pendingTransition.count} samples`
            : `${pendingTransition.t.action}: ${pendingTransition.sample.sample_code}`
          : ""}
        testid="transition-modal"
      >
        {pendingTransition && (
          <form onSubmit={confirmTransition} noValidate className="space-y-4">
            {!pendingTransition.bulk && (
              <p className="text-sm text-slate-600">
                <StatusBadge status={pendingTransition.sample.status} />
                <span className="mx-2 text-slate-400">→</span>
                <StatusBadge status={pendingTransition.t.to} />
              </p>
            )}
            {pendingTransition.bulk && (
              <p className="text-sm text-slate-600">
                Applies to every selected sample; records the action fails on are reported and left unchanged.
              </p>
            )}
            <Field
              label={pendingTransition.bulk || !pendingTransition.t.reason_required ? "Reason (required for holds, returns and disposal)" : "Reason (required)"}
              error={transitionError}
              errorTestId="transition-reason-error"
            >
              <textarea
                data-testid="transition-reason-input"
                value={transitionReason}
                onChange={(e) => { setTransitionReason(e.target.value); setTransitionError(""); }}
                rows={2}
                className={`${inputCls} ${transitionError ? inputErrorCls : ""}`}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setPendingTransition(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <PrimaryButton data-testid="confirm-transition-button" disabled={transitioning}>
                {pendingTransition.bulk ? pendingTransition.action : pendingTransition.t.action}
              </PrimaryButton>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={batchOpen} onClose={() => setBatchOpen(false)} title="Batch registration" testid="batch-modal">
        {batchResult ? (
          <div className="space-y-4">
            <div className="rounded-md bg-status-ok/10 border border-status-ok/30 p-3 text-sm text-status-ok">
              {batchResult.created_count} sample{batchResult.created_count === 1 ? "" : "s"} registered
              {batchResult.error_count > 0 && `, ${batchResult.error_count} rejected`}.
            </div>
            {batchResult.errors.length > 0 && (
              <div data-testid="batch-errors" className="rounded-md bg-status-critical/10 border border-status-critical/30 p-3 text-sm text-status-critical space-y-1">
                {batchResult.errors.map((e) => <div key={e.row}>Row {e.row + 1}: {e.detail}</div>)}
              </div>
            )}
            <div className="flex justify-end">
              <PrimaryButton type="button" onClick={() => setBatchOpen(false)}>Done</PrimaryButton>
            </div>
          </div>
        ) : (
          <form onSubmit={submitBatch} noValidate className="space-y-4">
            <Field label="Client (shared)">
              <select data-testid="batch-client-select" value={batchShared.client_id} onChange={(e) => setBatchShared({ ...batchShared, client_id: e.target.value })} className={inputCls}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Received date (shared)">
                <input type="date" max={todayStr()} value={batchShared.received_date} onChange={(e) => setBatchShared({ ...batchShared, received_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Default type">
                <select value={batchShared.sample_type} onChange={(e) => setBatchShared({ ...batchShared, sample_type: e.target.value })} className={inputCls}>{sampleTypes.map((t) => <option key={t}>{t}</option>)}</select>
              </Field>
            </div>
            <Field label="Required tests (shared)">
              <div className="max-h-32 overflow-y-auto rounded-md border border-slate-200 p-2 space-y-1">
                {assays.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm text-slate-700 px-1 py-0.5 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={batchTests.includes(a.id)} onChange={() => setBatchTests((prev) => prev.includes(a.id) ? prev.filter((x) => x !== a.id) : [...prev, a.id])} className="rounded border-slate-300" />
                    <span>{a.name}</span>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Rows, one sample per line: name, type (optional), position (optional)">
              <textarea
                data-testid="batch-rows-input"
                value={batchRows}
                onChange={(e) => setBatchRows(e.target.value)}
                rows={6}
                placeholder={"Aliquot 1\nAliquot 2, Plasma\nAliquot 3, Plasma, A3"}
                className={`${inputCls} font-mono text-xs`}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setBatchOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <PrimaryButton data-testid="submit-batch-button" disabled={batchSaving}>Register batch</PrimaryButton>
            </div>
          </form>
        )}
      </Modal>
    </Page>
  );
}
